export type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'admin-theme';

const THEME_VARS: Record<ThemeMode, Record<string, string>> = {
  light: {
    '--color-background': 'hsl(0 0% 100%)',
    '--color-foreground': 'hsl(222.2 84% 4.9%)',
    '--color-card': 'hsl(0 0% 100%)',
    '--color-card-foreground': 'hsl(222.2 84% 4.9%)',
    '--color-popover': 'hsl(0 0% 100%)',
    '--color-popover-foreground': 'hsl(222.2 84% 4.9%)',
    '--color-primary': 'hsl(222.2 47.4% 11.2%)',
    '--color-primary-foreground': 'hsl(210 40% 98%)',
    '--color-secondary': 'hsl(210 40% 96%)',
    '--color-secondary-foreground': 'hsl(222.2 84% 4.9%)',
    '--color-muted': 'hsl(210 40% 96%)',
    '--color-muted-foreground': 'hsl(215.4 16.3% 46.9%)',
    '--color-accent': 'hsl(210 40% 96%)',
    '--color-accent-foreground': 'hsl(222.2 84% 4.9%)',
    '--color-destructive': 'hsl(0 84.2% 60.2%)',
    '--color-destructive-foreground': 'hsl(210 40% 98%)',
    '--color-border': 'hsl(214.3 31.8% 91.4%)',
    '--color-input': 'hsl(214.3 31.8% 91.4%)',
    '--color-ring': 'hsl(222.2 84% 4.9%)',
  },
  dark: {
    '--color-background': 'hsl(222.2 84% 4.9%)',
    '--color-foreground': 'hsl(210 40% 98%)',
    '--color-card': 'hsl(222.2 84% 4.9%)',
    '--color-card-foreground': 'hsl(210 40% 98%)',
    '--color-popover': 'hsl(222.2 84% 4.9%)',
    '--color-popover-foreground': 'hsl(210 40% 98%)',
    '--color-primary': 'hsl(210 40% 98%)',
    '--color-primary-foreground': 'hsl(222.2 47.4% 11.2%)',
    '--color-secondary': 'hsl(217.2 32.6% 17.5%)',
    '--color-secondary-foreground': 'hsl(210 40% 98%)',
    '--color-muted': 'hsl(217.2 32.6% 17.5%)',
    '--color-muted-foreground': 'hsl(215 20.2% 65.1%)',
    '--color-accent': 'hsl(217.2 32.6% 17.5%)',
    '--color-accent-foreground': 'hsl(210 40% 98%)',
    '--color-destructive': 'hsl(0 62.8% 30.6%)',
    '--color-destructive-foreground': 'hsl(210 40% 98%)',
    '--color-border': 'hsl(217.2 32.6% 17.5%)',
    '--color-input': 'hsl(217.2 32.6% 17.5%)',
    '--color-ring': 'hsl(212.7 26.8% 83.9%)',
  },
};

export function getPreferredTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'light';

  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') {
    return stored;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function applyTheme(mode: ThemeMode) {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;

  Object.entries(THEME_VARS[mode]).forEach(([token, value]) => {
    root.style.setProperty(token, value);
  });

  root.style.setProperty('color-scheme', mode);
  root.classList.toggle('dark', mode === 'dark');
  root.setAttribute('data-theme', mode);
}

export function persistTheme(mode: ThemeMode) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(THEME_STORAGE_KEY, mode);
}
